<template>
  <div class="modal-card" style="width: auto">
    <header class="modal-card-head">
      <p class="modal-card-title has-text-centered">
        {{ $t("orders.detailsTitle") }}
      </p>
    </header>

    <section class="modal-card-body">
      <h2 class="has-text-centered">
        <p>{{ order.store.storeInfo.name }}</p>
        <b>{{ $t("orders.orderNumber") }}:</b> {{ order.orderId || "-" }}
        <br />
        <b>{{ $t("orders.dateTime") }}:</b>
        {{
          moment
            .utc(order.createdAt)
            .add(preferences.timezone, "h")
            .format("DD/MM/YY HH:mm")
        }}
        <br />
        <b>{{ $t("orders.orderId") }}:</b> {{ order._id }}
        <br />
        <b>{{ $t("orders.description") }}:</b> {{ order.description || "-" }}
      </h2>
      <hr />

      <h2 style="margin-bottom: 1rem;">
        <b>{{ $tc("orders.Items", 2) }}</b>
      </h2>

      <div
        v-for="item in order.cart.items"
        :key="item._id"
        class="columns"
        style="margin-bottom: 0;"
      >
        <div class="column is-narrow">{{ item.quantity }} x</div>

        <div class="column">
          {{ item.name }}
        </div>

        <div class="column is-narrow">
          {{ `${payments.currencySymbol} ${formatMoney(item.price)}` }}
        </div>
      </div>

      <div class="columns" style="margin-bottom: 0;">
        <div class="column">
          <b>{{ $t("orders.cartTotal") }}</b>
        </div>

        <div class="column is-narrow has-text-right">
          <b>
            {{
              `${payments.currencySymbol} ${formatMoney(order.cart.totalValue)}`
            }}
          </b>
        </div>
      </div>

      <div class="columns" style="margin-bottom: 0;">
        <div class="column">
          {{ $t("orders.fees") }} ({{ order.details.royaltyPercentage * 100 }}%)
        </div>

        <div class="column is-narrow has-text-right">
          {{
            `- ${payments.currencySymbol} ${formatMoney(
              order.details.royaltyValue
            )}`
          }}
        </div>
      </div>

      <div class="columns" style="margin-bottom: 0;">
        <div class="column">
          <b>{{ $t("orders.valueToReceive") }}</b>
        </div>

        <div class="column is-narrow has-text-right">
          <b>
            {{
              `${payments.currencySymbol} ${formatMoney(
                order.cart.totalValue - order.details.royaltyValue
              )}`
            }}
          </b>
        </div>
      </div>

      <hr />

      <p>
        <b>{{ $t("orders.paymentLink") }}:</b>
        <a :href="`${order.paymentLink}`" target="_blank">
          {{ order.paymentLink }}
        </a>
        <b-button
          @click.stop="copyPaymentLink(order.paymentLink)"
          class="is-small"
          style="position: relative; left: 4px; bottom: 3px;"
        >
          <b-icon icon="copy"></b-icon>
        </b-button>
      </p>

      <p>
        <b>{{ $t("orders.maxInstallments") }}:</b>
        {{ order.details.maxInstallments }}
      </p>

      <b-button
        :type="{
          'is-success': order.details.status === 'success',
          'is-light': order.details.status === 'cancelled',
          'is-dark': order.details.status === 'refunded',
          'is-danger': order.details.status === 'chargeback',
          'is-sea': order.details.status === 'pending'
        }"
        :disabled="order.details.status === 'cancelled'"
        slot="trigger"
        style="cursor: default; margin-top: 0.75rem;"
      >
        {{
          order.details.status === "success"
            ? $t("orders.statusSuccess")
            : order.details.status === "cancelled"
            ? $t("orders.statusCancelled")
            : order.details.status === "pending"
            ? $t("orders.statusPending")
            : order.details.status === "refundedRequested"
            ? $t("orders.statusRefundRequested")
            : order.details.status === "refunded"
            ? $t("orders.statusRefunded")
            : order.details.status === "refundRefused"
            ? $t("orders.statusRefundRefused")
            : order.details.status === "refundFailed"
            ? $t("orders.statusRefundFailed")
            : order.details.status === "refundReversed"
            ? $t("orders.statusRefundReversed")
            : order.details.status === "chargeback"
            ? $t("orders.statusChargeback")
            : "-"
        }}
      </b-button>

      <div
        v-if="order.details.status === 'success'"
        style="margin-top: 0.75rem"
      >
        <p>
          <b>{{ $t("orders.paidAt") }}:</b>
          {{ moment(order.paidAt).format("DD/MM/YYYY HH:mm") }}
        </p>

        <p>
          <b>{{ $t("orders.fundingSource") }}:</b>
          {{
            order.details.fundingSource === "credit"
              ? `${$t("orders.credit")} ${order.details.installments}x`
              : order.details.fundingSource === "debit"
              ? `${$t("orders.debit")}`
              : order.details.fundingSource === "pix"
              ? `${$t("orders.pix")}`
              : order.details.fundingSource === "voucher"
              ? `${$t("orders.voucher")}`
              : "-"
          }}
        </p>

        <div>
          <b>{{ $t("orders.settlements") }}:</b>
          <p v-for="settlement in order.settlementValues" :key="settlement._id">
            {{
              `${moment(settlement.settlementDate).format("DD/MM/YYYY")} - ${
                payments.currencySymbol
              } ${formatMoney(
                settlement.installmentValue - settlement.details.royaltyValue
              )}`
            }}
          </p>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot">
      <button class="button" type="button" @click="$parent.close()">
        {{ $t("common.close") }}
      </button>
    </footer>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { mask } from "../../mixins/mask";
import { common } from "../../mixins/common";

export default {
  props: ["order"],

  mixins: [mask, common],

  computed: {
    ...mapGetters(["preferences", "payments"])
  },

  methods: {
    copyPaymentLink(paymentLink) {
      const doc = document;

      doc.querySelector(`#paymentlink`).innerHTML = paymentLink;
      const clone = doc.querySelector(`#paymentlink`).cloneNode(true);
      clone.style = "position: absolute; left: -1000px; top: -1000px";
      clone.id = "clone";
      clone.innerHTML = paymentLink;
      doc.body.appendChild(clone);

      const text = doc.querySelector(`#clone`);
      let range;
      let selection;

      if (doc.body.createTextRange) {
        range = doc.body.createTextRange();
        range.moveToElement(text);
        range.select();
      } else if (window.getSelection) {
        selection = window.getSelection();
        range = doc.createRange();
        range.selectNodeContents(text);
        selection.removeAllRanges();
        selection.addRange(range);
      }

      try {
        if (document.execCommand("copy")) {
          this.$buefy.toast.open({
            message: this.$t("orders.linkCopied"),
            type: "is-primary"
          });
        } else {
          this.alertError({ message: "COPY_FAILED" });
        }
      } catch (err) {
        this.alertError({ message: "COPY_FAILED" });
      }

      window.getSelection().removeAllRanges();
      doc.body.removeChild(clone);
    }
  }
};
</script>
